import { useState } from 'react'

import css from './sass/endedauction.module.scss'
import themeCss from '../components/sass/theme.module.scss'

function sanitizeForSpreadsheet(value) {
  if (value === null || value === undefined) return ''

  return String(value)
    .replace(/\r?\n|\r/g, ' ')
    .replace(/\t/g, ' ')
}

function formatYesNo(value) {
  return value === true ? 'Yes' : 'No'
}

function getFullName(user = {}) {
  return sanitizeForSpreadsheet([user.FirstName, user.LastName].filter(Boolean).join(' '))
}

function buildRegistrationLookup(registrations = []) {
  return registrations.reduce((lookup, registration) => {
    const user = registration?.User

    if (user?.UserId) {
      lookup[user.UserId] = {
        fullName: getFullName(user),
        email: sanitizeForSpreadsheet(user.ContactEmail),
        phone: sanitizeForSpreadsheet(user.ContactPhone),
      }
    }

    return lookup
  }, {})
}

function createRow(values) {
  return values.map(sanitizeForSpreadsheet).join('\t')
}

function buildBidItemsSection(auction, registrationLookup) {
  const headers = [
    'Item Title',
    'Current High Bidder',
    'Fair Market Value',
    'Current High Bid',
    'Paid?',
    'Current High Bidder Email',
    "Current High Bidder's Phone Number",
  ]

  const rows = (auction?.Items || []).map((item) => {
    const bidder =
      registrationLookup[item?.CurrentHighBidder] || {
        fullName: getFullName(item?.CurrentHighBidderInfo),
        email: sanitizeForSpreadsheet(item?.CurrentHighBidderInfo?.ContactEmail),
        phone: sanitizeForSpreadsheet(item?.CurrentHighBidderInfo?.ContactPhone),
      }

    return createRow([
      item?.Title,
      bidder.fullName,
      item?.Value,
      item?.CurrentBid,
      formatYesNo(item?.Paid),
      bidder.email,
      bidder.phone,
    ])
  })

  return ['Bid Items', createRow(headers), ...rows]
}

function buildFixedItemsSection(auction, registrationLookup) {
  const headers = [
    'Purchaser Name',
    'Item Title',
    'Amount Purchased',
    'Fair Market Value',
    'Purchase Price',
    'Total Amount Owed',
    'Paid?',
    "Purchaser's Email Address",
    "Purchaser's Phone Number",
  ]

  const rows = []

  ;(auction?.FixedItems || []).forEach((fixedItem) => {
    const purchaseTotals = {}
    const paymentTotals = {}

    ;(fixedItem?.Purchases || []).forEach((purchase) => {
      if (!purchase?.UserId) return

      if (!purchaseTotals[purchase.UserId]) {
        purchaseTotals[purchase.UserId] = {
          quantity: 0,
          fullName: sanitizeForSpreadsheet(purchase.FullName),
        }
      }

      purchaseTotals[purchase.UserId].quantity += Number(purchase.Quantity) || 0

      if (!purchaseTotals[purchase.UserId].fullName && purchase.FullName) {
        purchaseTotals[purchase.UserId].fullName = sanitizeForSpreadsheet(purchase.FullName)
      }
    })

    ;(fixedItem?.Payments || []).forEach((payment) => {
      if (!payment?.UserId) return
      paymentTotals[payment.UserId] = (paymentTotals[payment.UserId] || 0) + (Number(payment.Quantity) || 0)
    })

    Object.entries(purchaseTotals).forEach(([userId, purchaseInfo]) => {
      const registration = registrationLookup[userId] || {}
      const amountPurchased = purchaseInfo.quantity
      const purchasePrice = Number(fixedItem?.PurchasePrice) || 0

      rows.push(
        createRow([
          registration.fullName || purchaseInfo.fullName,
          fixedItem?.Title,
          amountPurchased,
          fixedItem?.Value,
          fixedItem?.PurchasePrice,
          amountPurchased * purchasePrice,
          amountPurchased === (paymentTotals[userId] || 0) ? 'Yes' : 'No',
          registration.email,
          registration.phone,
        ])
      )
    })
  })

  return ['Fixed Price Items', createRow(headers), ...rows]
}

function buildDonationsSection(auction, registrationLookup) {
  const hasDonation = auction?.HasDonation === true || auction?.hasDonation === true

  if (!hasDonation) return []

  const headers = [
    'Attendee Name',
    'Donation Amount',
    'Paid?',
    "Attendee's Email Address",
    "Attendee's Phone Number",
  ]

  const rows = (auction?.Registrations || [])
    .filter((registration) => Boolean(registration?.DonationTotal))
    .map((registration) => {
      const userId = registration?.User?.UserId
      const attendee = registrationLookup[userId] || {
        fullName: getFullName(registration?.User),
        email: sanitizeForSpreadsheet(registration?.User?.ContactEmail),
        phone: sanitizeForSpreadsheet(registration?.User?.ContactPhone),
      }

      return createRow([
        attendee.fullName,
        registration?.DonationTotal,
        registration?.DonationPayment ? 'Yes' : 'No',
        attendee.email,
        attendee.phone,
      ])
    })

  return ['Donations', createRow(headers), ...rows]
}

function buildReportText(auction) {
  const registrationLookup = buildRegistrationLookup(auction?.Registrations)
  const sections = [
    buildBidItemsSection(auction, registrationLookup),
    buildFixedItemsSection(auction, registrationLookup),
    buildDonationsSection(auction, registrationLookup),
  ].filter((section) => section.length > 0)

  return sections.map((section) => section.join('\n')).join('\n\n\n')
}

export default function ReportButton({ auction }) {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)

  const copyReportToClipboard = async () => {
    const reportText = buildReportText(auction)

    try {
      await navigator.clipboard.writeText(reportText)
      setCopyError(false)
      setCopied(true)

      setTimeout(() => {
        setCopied(false)
      }, 1200)
    } catch (error) {
      console.error('Unable to copy full report', error)
      setCopied(false)
      setCopyError(true)
    }
  }

  if (auction.Items.length === 0 && auction.FixedItems.length === 0) return <></>

  return (
    <div className={css['report-button-container']}>
      <button type="button" className={`${themeCss['FilledBlue']} ${themeCss['button']}`} onClick={copyReportToClipboard}>
        Copy Full Report
      </button>

      {copied && <p>Copied full report to clipboard.</p>}
      {copyError && <p className={`${css['report-button-feedback-error']}`}>Unable to copy report. Please check browser clipboard permissions.</p>}
    </div>
  )
}
